import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Save, ArrowLeft } from 'lucide-react';
import { validateDateBR } from '../../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const Ciclo180Form = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [competencias, setCompetencias] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    periodo_inicio: '',
    periodo_fim: '',
    publico_alvo: 'todos',
    areas_selecionadas: [],
    competencias_ids: [],
    escala_min: 1,
    escala_max: 5,
    anonimato: false,
    peso_auto: 40,
    peso_gestor: 60,
    permitir_comentarios: true,
    integrar_pdi: true
  });

  useEffect(() => {
    fetchCompetencias();
    if (isEditing) {
      fetchCiclo();
    }
  }, [id]);

  const fetchCompetencias = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/competencias`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCompetencias(response.data);
    } catch (error) {
      console.error('Erro ao buscar competências:', error);
    }
  };

  const fetchCiclo = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback-180/ciclos/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFormData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar ciclo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.periodo_inicio || !formData.periodo_fim) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!validateDateBR(formData.periodo_inicio, { pastYears: 15, futureYears: 15 }) || !validateDateBR(formData.periodo_fim, { pastYears: 15, futureYears: 15 })) {
      toast.error('Data de início ou fim inválida');
      return;
    }
    if (formData.periodo_fim < formData.periodo_inicio) {
      toast.error('A data fim não pode ser menor que a data início');
      return;
    }

    try {
      setLoading(true);
      
      if (isEditing) {
        await axios.put(`${API}/api/feedback-180/ciclos/${id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Ciclo atualizado com sucesso!');
      } else {
        await axios.post(`${API}/api/feedback-180/ciclos`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Ciclo criado com sucesso!');
      }
      
      navigate('/feedback/cultural');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar ciclo');
    } finally {
      setLoading(false);
    }
  };

  const toggleCompetencia = (compId) => {
    setFormData(prev => ({
      ...prev,
      competencias_ids: prev.competencias_ids.includes(compId)
        ? prev.competencias_ids.filter(id => id !== compId)
        : [...prev.competencias_ids, compId]
    }));
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Button
        variant="outline"
        onClick={() => navigate('/feedback/cultural')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isEditing ? 'Editar' : 'Novo'} Ciclo de Feedback 180º</CardTitle>
          <CardDescription>
            Configure o período, competências e regras do ciclo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações Básicas */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2">Informações Básicas</h3>
              
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do Ciclo *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="Ex: Feedback 180º - 2º Semestre 2025"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder="Descreva o objetivo deste ciclo"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="periodo_inicio">Data Início *</Label>
                  <Input
                    id="periodo_inicio"
                    type="date"
                    value={formData.periodo_inicio}
                    onChange={(e) => setFormData({...formData, periodo_inicio: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="periodo_fim">Data Fim *</Label>
                  <Input
                    id="periodo_fim"
                    type="date"
                    value={formData.periodo_fim}
                    onChange={(e) => setFormData({...formData, periodo_fim: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Competências */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2">Competências Avaliadas</h3>
              
              {competencias.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-amber-800">
                      Nenhuma competência cadastrada. Crie competências primeiro.
                    </p>
                    <Button
                      type="button"
                      onClick={() => navigate('/feedback/180/competencias')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Criar Competências
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-4">
                  {competencias.map((comp) => (
                    <div key={comp.id} className="flex items-start space-x-2">
                      <Checkbox
                        id={`comp-${comp.id}`}
                        checked={formData.competencias_ids.includes(comp.id)}
                        onCheckedChange={() => toggleCompetencia(comp.id)}
                      />
                      <div className="flex-1">
                        <Label htmlFor={`comp-${comp.id}`} className="font-medium">
                          {comp.nome}
                        </Label>
                        <p className="text-xs text-gray-600">{comp.descricao}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                Selecionadas: {formData.competencias_ids.length} competência(s)
              </p>
            </div>

            {/* Configurações */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium border-b pb-2">Configurações</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="peso_auto">Peso Autoavaliação (%)</Label>
                  <Input
                    id="peso_auto"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.peso_auto}
                    onChange={(e) => setFormData({...formData, peso_auto: parseFloat(e.target.value)})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="peso_gestor">Peso Gestor (%)</Label>
                  <Input
                    id="peso_gestor"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.peso_gestor}
                    onChange={(e) => setFormData({...formData, peso_gestor: parseFloat(e.target.value)})}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="permitir_comentarios"
                    checked={formData.permitir_comentarios}
                    onCheckedChange={(checked) => setFormData({...formData, permitir_comentarios: checked})}
                  />
                  <Label htmlFor="permitir_comentarios">
                    Permitir comentários qualitativos
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="integrar_pdi"
                    checked={formData.integrar_pdi}
                    onCheckedChange={(checked) => setFormData({...formData, integrar_pdi: checked})}
                  />
                  <Label htmlFor="integrar_pdi">
                    Integrar com PDI (Plano de Desenvolvimento Individual)
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="anonimato"
                    checked={formData.anonimato}
                    onCheckedChange={(checked) => setFormData({...formData, anonimato: checked})}
                  />
                  <Label htmlFor="anonimato">
                    Permitir feedbacks anônimos
                  </Label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/feedback/cultural')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading || formData.competencias_ids.length === 0}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Salvando...' : isEditing ? 'Atualizar' : 'Criar Ciclo'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Ciclo180Form;
